<template>
  <div class="projects-container">
    <div class="page-header">
      <h1>服务空间</h1>
      <el-button v-if="canCreateProject" type="primary" icon="el-icon-plus" @click="showCreateDialog = true">
        创建服务空间
      </el-button>
      <el-tooltip v-else content="您的账户等级无法创建项目，请联系管理员升级" placement="bottom">
        <el-button type="info" icon="el-icon-plus" disabled>
          创建服务空间
        </el-button>
      </el-tooltip>
    </div>

    <div class="projects-content">
      <div class="projects-grid">
        <el-card 
          v-for="project in paginatedProjects" 
          :key="project.id" 
          class="project-card"
          shadow="hover"
          @click.native="enterSpace(project)"
        >
          <div class="project-card-header">
            <div class="project-icon">
              <i class="el-icon-s-platform"></i>
            </div>
            <div class="project-info">
              <h3>{{ project.name }}</h3>
              <p class="project-id">SpaceID: {{ project.id }}</p>
            </div>
          </div>
          <div class="api-key-section">
            <div class="api-key-label">API Key:</div>
            <div class="api-key-value">
              <code>{{ project.api_key || '生成中...' }}</code>
              <el-button 
                v-if="project.api_key"
                type="text" 
                size="mini" 
                icon="el-icon-document-copy"
                @click.stop="copyApiKey(project.api_key)"
                class="copy-btn"
              >
                复制
              </el-button>
            </div>
          </div>
          <div class="project-description">
            {{ project.description || '暂无描述' }}
          </div>
          <div class="project-meta">
            <span class="meta-item">
              <i class="el-icon-time"></i>
              {{ formatDate(project.created_at) }}
            </span>
            <el-tag :type="project.status === 1 ? 'success' : 'info'" size="small">
              {{ project.status === 1 ? '运行中' : '已停用' }}
            </el-tag>
          </div>
          <div class="project-actions" @click.stop>
            <el-button v-if="$canWrite()" type="text" size="small" @click="editProject(project)">
              <i class="el-icon-edit"></i> 编辑
            </el-button>
            <el-button v-if="$canWrite()" type="text" size="small" @click="deleteProject(project.id)" style="color: #F56C6C;">
              <i class="el-icon-delete"></i> 删除
            </el-button>
          </div>
        </el-card>

        <!-- 空状态 -->
        <div v-if="projects.length === 0 && !loading" class="empty-state">
          <i class="el-icon-folder-opened"></i>
          <p>还没有服务空间</p>
          <el-button v-if="canCreateProject" type="primary" @click="showCreateDialog = true">创建第一个服务空间</el-button>
          <p v-else class="no-permission-tip">您的账户等级无法创建项目，请联系管理员升级</p>
        </div>
      </div>
      
      <!-- 分页固定在底部 -->
      <div class="pagination-footer">
        <el-pagination
          @current-change="handlePageChange"
          :current-page="pagination.currentPage"
          :page-size="pagination.pageSize"
          layout="total, prev, pager, next, jumper"
          :total="totalProjects"
        ></el-pagination>
      </div>
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog
      :title="dialogMode === 'create' ? '创建服务空间' : '编辑服务空间'"
      :visible.sync="showCreateDialog"
      width="500px"
    >
      <el-form :model="form" :rules="rules" ref="projectForm" label-width="100px">
        <el-form-item label="空间名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入服务空间名称"></el-input>
        </el-form-item>
        <el-form-item label="空间描述" prop="description">
          <el-input
            type="textarea"
            v-model="form.description"
            placeholder="请输入服务空间描述"
            :rows="4"
          ></el-input>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showCreateDialog = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ProjectList',
  data() {
    return {
      projects: [],
      loading: false,
      submitting: false,
      showCreateDialog: false,
      dialogMode: 'create',
      currentProjectId: null,
      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 12
      },
      form: {
        name: '',
        description: '',
        status: 1
      },
      rules: {
        name: [
          { required: true, message: '请输入服务空间名称', trigger: 'blur' }
        ]
      }
    };
  },
  computed: {
    canCreateProject() {
      const user = this.$store.state.user;
      // 00-管理员，01-VIP用户 可以创建项目
      return user && (user.level === '00' || user.level === '01');
    },
    paginatedProjects() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
      const end = start + this.pagination.pageSize;
      return this.projects.slice(start, end);
    },
    totalProjects() {
      return this.projects.length;
    }
  },
  mounted() {
    this.fetchProjects();
  },
  methods: {
    async fetchProjects() {
      this.loading = true;
      try {
        const response = await this.$axios.get('/projects');
        this.projects = response.data;
      } catch (error) {
        this.$message.error('获取服务空间列表失败');
      } finally {
        this.loading = false;
      }
    },
    enterSpace(project) {
      // 保存当前选中的服务空间到 store
      this.$store.commit('SET_CURRENT_PROJECT', project);
      // 跳转到服务空间详情页
      this.$router.push(`/space/${project.id}/overview`);
    },
    editProject(project) {
      this.dialogMode = 'edit';
      this.currentProjectId = project.id;
      this.form = {
        name: project.name,
        description: project.description,
        status: project.status
      };
      this.showCreateDialog = true;
    },
    submitForm() {
      this.$refs.projectForm.validate(async (valid) => {
        if (valid) {
          this.submitting = true;
          try {
            if (this.dialogMode === 'create') {
              await this.$axios.post('/projects', this.form);
              this.$message.success('创建成功');
            } else {
              await this.$axios.put(`/projects/${this.currentProjectId}`, this.form);
              this.$message.success('更新成功');
            }
            this.showCreateDialog = false;
            this.resetForm();
            this.fetchProjects();
          } catch (error) {
            this.$message.error(error.response?.data?.error || '操作失败');
          } finally {
            this.submitting = false;
          }
        }
      });
    },
    async deleteProject(id) {
      try {
        await this.$confirm('确定要删除该服务空间吗？删除后数据将无法恢复。', '提示', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        });
        await this.$axios.delete(`/projects/${id}`);
        this.$message.success('删除成功');
        this.fetchProjects();
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败');
        }
      }
    },
    resetForm() {
      this.form = {
        name: '',
        description: '',
        status: 1
      };
      this.dialogMode = 'create';
      this.currentProjectId = null;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN');
    },
    copyApiKey(apiKey) {
      // 创建临时输入框
      const input = document.createElement('input');
      input.value = apiKey;
      document.body.appendChild(input);
      input.select();
      
      try {
        // 执行复制
        document.execCommand('copy');
        this.$message.success('API Key 已复制到剪贴板');
      } catch (err) {
        // 如果浏览器支持 Clipboard API，使用它
        if (navigator.clipboard) {
          navigator.clipboard.writeText(apiKey).then(() => {
            this.$message.success('API Key 已复制到剪贴板');
          }).catch(() => {
            this.$message.error('复制失败，请手动复制');
          });
        } else {
          this.$message.error('复制失败，请手动复制');
        }
      } finally {
        document.body.removeChild(input);
      }
    },
    
    // 页码改变
    handlePageChange(page) {
      this.pagination.currentPage = page;
    }
  }
};
</script>

<style scoped>
.projects-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.page-header h1 {
  font-size: 24px;
  font-weight: 500;
  color: #303133;
  margin: 0;
}

.projects-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.projects-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  overflow-y: auto;
  align-content: start;
}

/* 分页固定在底部 */
.pagination-footer {
  padding: 16px 0;
  border-top: 1px solid #e4e7ed;
  background: white;
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.project-card {
  cursor: pointer;
  transition: all 0.3s;
  border-radius: 8px;
}

.project-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.project-card-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.project-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.project-icon i {
  font-size: 24px;
  color: white;
}

.project-info h3 {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
  margin: 0 0 4px 0;
}

.project-id {
  font-size: 12px;
  color: #909399;
  margin: 0;
}

.api-key-section {
  background: #f5f7fa;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 15px;
}

.api-key-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
  font-weight: 500;
}

.api-key-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.api-key-value code {
  flex: 1;
  background: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  color: #303133;
  border: 1px solid #DCDFE6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
}

.copy-btn {
  padding: 4px 8px;
  color: #409EFF;
}

.copy-btn:hover {
  color: #66b1ff;
}

.project-description {
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 15px;
  min-height: 44px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.project-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #EBEEF5;
  margin-bottom: 10px;
}

.meta-item {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
}

.meta-item i {
  margin-right: 4px;
}

.project-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid #EBEEF5;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  color: #DCDFE6;
  margin-bottom: 20px;
}

.empty-state p {
  font-size: 16px;
  margin-bottom: 20px;
}

.no-permission-tip {
  color: #E6A23C;
  font-size: 14px;
}
</style>
